import React, { useState, useEffect } from 'react';
import {
  GraduationCap, RefreshCw,
  Plus, Edit, X,
  ChevronLeft, ChevronRight, ArrowUpDown, ChevronUp, ChevronDown
} from 'lucide-react';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import { CountBadge } from '../../../../components/UI/CountBadge';
import { FilterButton } from '../../../../components/UI/FilterButton';
import EducationInstitutionForm from '../modals/EducationInstitutionForm';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import { configAPI } from '../../../../services/api';
import toast from '../../../../utils/toast';

const EducationInstitutionsTab = ({ onStatsUpdate }) => {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedInstitutionType, setSelectedInstitutionType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Modal State
  const [showInstitutionForm, setShowInstitutionForm] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState(null);
  
  // Institution Types (will be loaded from API)
  const [institutionTypes, setInstitutionTypes] = useState([]);

  useEffect(() => {
    loadInstitutions();
    loadInstitutionTypes();
  }, []);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      // Request large page size to get all institutions (MAX_PAGE_SIZE is 50000)
      const response = await configAPI.getEducationInstitutions({ page_size: 10000 });
      const data = response.data?.results || response.data || [];
      setInstitutions(data);
    } catch (error) {
      console.error('Error loading education institutions:', error);
      toast.error('Failed to load education institutions');
    } finally {
      setLoading(false);
    }
  };

  const loadInstitutionTypes = async () => {
    try {
      const response = await configAPI.getInstitutionTypes();
      const data = response.data?.results || response.data || [];
      setInstitutionTypes(data);
    } catch (error) {
      console.error('Error loading institution types:', error);
    }
  };

  const handleSyncHipo = async () => {
    try {
      setSyncing(true);
      const response = await fetch('/api/v1/configuration/education-institutions/sync_hipo/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to queue sync');
      }
      
      const data = await response.json();
      if (data.status === 'queued') {
        toast.success('Hipo API sync queued. This may take a few minutes.');
        // Reload after a delay to see new data
        setTimeout(() => {
          loadInstitutions();
        }, 5000);
      }
    } catch (error) {
      console.error('Error syncing Hipo API:', error);
      toast.error(error.message || 'Failed to sync from Hipo API');
    } finally {
      setSyncing(false);
    }
  };

  // Filter institutions
  const filteredInstitutions = institutions.filter(institution => {
    const matchesSearch = 
      institution.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institution.moe_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institution.uen?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institution.address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSource = selectedSource === 'all' || institution.source === selectedSource;
    const matchesCountry = selectedCountry === 'all' || institution.country === selectedCountry || institution.alpha_two_code === selectedCountry;
    const matchesType = selectedInstitutionType === 'all' || institution.institution_type === selectedInstitutionType;
    
    return matchesSearch && matchesSource && matchesCountry && matchesType;
  });

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-3 h-3 ml-1 text-red-600" />
      : <ChevronDown className="w-3 h-3 ml-1 text-red-600" />;
  };

  // Sort institutions
  const sortedInstitutions = [...filteredInstitutions].sort((a, b) => {
    const multiplier = sortConfig.direction === 'asc' ? 1 : -1;
    
    switch (sortConfig.key) {
      case 'name':
        return multiplier * (a.name || '').localeCompare(b.name || '');
      case 'country':
        return multiplier * (a.country || '').localeCompare(b.country || '');
      case 'type':
        const typeA = institutionTypes.find(t => t.id === a.institution_type)?.name || '';
        const typeB = institutionTypes.find(t => t.id === b.institution_type)?.name || '';
        return multiplier * typeA.localeCompare(typeB);
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedInstitutions.length / itemsPerPage);
  const paginatedInstitutions = sortedInstitutions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get source badge
  const getSourceBadge = (source) => {
    const badges = {
      'hipo_api': { label: 'Hipo API', status: 'in_progress' },
      'manual': { label: 'Manual', status: 'draft' },
      'legacy': { label: 'Legacy', status: 'pending' },
      'seed': { label: 'Seeded', status: 'approved' }
    };
    const config = badges[source] || { label: source || 'Unknown', status: 'draft' };
    return <StatusBadge status={config.status} label={config.label} />;
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSource, selectedCountry, selectedInstitutionType]);

  const hasActiveFilters = searchTerm || selectedSource !== 'all' || selectedCountry !== 'all' || selectedInstitutionType !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSource('all');
    setSelectedCountry('all');
    setSelectedInstitutionType('all');
  };

  const handleInstitutionSave = async () => {
    await loadInstitutions();
    onStatsUpdate && onStatsUpdate();
    setShowInstitutionForm(false);
    setEditingInstitution(null);
  };

  // Get unique countries for filter
  const uniqueCountries = [...new Set(institutions.map(i => i.country).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Institution Form Modal */}
      <EducationInstitutionForm
        institution={editingInstitution}
        isOpen={showInstitutionForm}
        onClose={() => {
          setShowInstitutionForm(false);
          setEditingInstitution(null);
        }}
        onSave={handleInstitutionSave}
        institutionTypes={institutionTypes}
      />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate min-w-0">Education Institution Library</h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button 
            onClick={handleSyncHipo}
            disabled={syncing}
            className="icon-button-admin"
            title={syncing ? 'Syncing from Hipo Labs University API...' : 'Sync from Hipo Labs University API'}
            aria-label="Sync Hipo API"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          </button>
          <button 
            onClick={() => {
              setEditingInstitution(null);
              setShowInstitutionForm(true);
            }}
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            aria-label="Add Institution"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Institution</span>
          </button>
        </div>
      </div>

      {/* Search and Toolbar */}
      <div className="card">
        <div className="card-content">
          {/* Main Toolbar Row */}
          <div className="flex items-center justify-between gap-4">
            {/* Search */}
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search by name, MOE code, UEN, or address..."
              className="max-w-xl"
            />
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={(searchTerm ? 1 : 0) + (selectedSource !== 'all' ? 1 : 0) + (selectedCountry !== 'all' ? 1 : 0) + (selectedInstitutionType !== 'all' ? 1 : 0)}
                onClick={() => setShowFilters(!showFilters)}
              />
            </div>
          </div>

          {/* Expandable Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Data Source</label>
                  <select
                    value={selectedSource}
                    onChange={(e) => setSelectedSource(e.target.value)}
                    className="select-admin w-full"
                  >
                    <option value="all">All Sources</option>
                    <option value="hipo_api">Hipo API</option>
                    <option value="manual">Manual Entry</option>
                    <option value="legacy">Legacy Data</option>
                    <option value="seed">Seeded Data</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="select-admin w-full"
                  >
                    <option value="all">All Countries</option>
                    {uniqueCountries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Institution Type</label>
                  <select
                    value={selectedInstitutionType}
                    onChange={(e) => setSelectedInstitutionType(e.target.value)}
                    className="select-admin w-full"
                  >
                    <option value="all">All Types</option>
                    {institutionTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {searchTerm && (
                <span className="pill pill-secondary flex items-center gap-1 max-w-[200px]">
                  <span className="truncate">Search: "{searchTerm}"</span>
                  <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-red-600 flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedSource !== 'all' && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Source: {selectedSource}
                  <button onClick={() => setSelectedSource('all')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedCountry !== 'all' && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Country: {selectedCountry}
                  <button onClick={() => setSelectedCountry('all')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedInstitutionType !== 'all' && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Type: {institutionTypes.find(t => t.id === parseInt(selectedInstitutionType))?.name || selectedInstitutionType}
                  <button onClick={() => setSelectedInstitutionType('all')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button onClick={clearFilters} className="text-xs text-red-600 hover:underline ml-2">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 text-sm text-gray-500">
        <span>Showing {paginatedInstitutions.length} of {sortedInstitutions.length} institutions</span>
        {totalPages > 1 && (
          <span>Page {currentPage} of {totalPages}</span>
        )}
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-3">
        {paginatedInstitutions.map((institution) => (
          <div key={institution.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{institution.name}</div>
                {institution.moe_code && (
                  <div className="text-xs text-gray-500">MOE: {institution.moe_code}</div>
                )}
              </div>
              <div className="flex-shrink-0">
                {getSourceBadge(institution.source)}
              </div>
            </div>
            <div className="mt-3 space-y-2 flex-1">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Country</span>
                <span className="text-gray-900 text-right truncate min-w-0">
                  {institution.country || '—'}
                  {institution.alpha_two_code ? ` (${institution.alpha_two_code})` : ''}
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">Type</span>
                <span className="text-right">
                  <StatusBadge
                    status="draft"
                    label={institutionTypes.find(t => t.id === institution.institution_type)?.name || '—'}
                  />
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500 flex-shrink-0">UEN</span>
                <span className="text-gray-900 text-right truncate min-w-0 font-mono text-xs">
                  {institution.uen || '—'}
                </span>
              </div>
            </div>
            <div className="card-action-footer justify-end gap-2">
              <button
                onClick={() => {
                  setEditingInstitution(institution);
                  setShowInstitutionForm(true);
                }}
                className="icon-button-admin"
                title="Edit Institution"
                aria-label="Edit Institution"
              >
                <Edit className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Institution Table */}
      <div className="card hidden md:block">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  <button 
                    onClick={() => handleSort('name')}
                    className="flex items-center hover:text-red-600 transition-colors"
                  >
                    Institution
                    {getSortIcon('name')}
                  </button>
                </th>
                <th className="table-header-cell">
                  <button 
                    onClick={() => handleSort('country')}
                    className="flex items-center hover:text-red-600 transition-colors"
                  >
                    Country
                    {getSortIcon('country')}
                  </button>
                </th>
                <th className="table-header-cell">
                  <button 
                    onClick={() => handleSort('type')}
                    className="flex items-center hover:text-red-600 transition-colors"
                  >
                    Type
                    {getSortIcon('type')}
                  </button>
                </th>
                <th className="table-header-cell">Source</th>
                <th className="table-header-cell">UEN</th>
                <th className="table-header-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {paginatedInstitutions.map((institution) => (
                <tr key={institution.id} className="table-row">
                  <td className="table-cell">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {institution.name}
                      </div>
                      {institution.moe_code && (
                        <div className="text-xs text-gray-500">
                          MOE: {institution.moe_code}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-900">{institution.country}</span>
                      {institution.alpha_two_code && (
                        <span className="text-xs text-gray-500">({institution.alpha_two_code})</span>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <StatusBadge
                      status="draft"
                      label={institutionTypes.find(t => t.id === institution.institution_type)?.name || '—'}
                    />
                  </td>
                  <td className="table-cell">
                    {getSourceBadge(institution.source)}
                  </td>
                  <td className="table-cell">
                    {institution.uen ? (
                      <span className="text-xs text-gray-600 font-mono">{institution.uen}</span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingInstitution(institution);
                          setShowInstitutionForm(true);
                        }}
                        className="icon-button-admin"
                        title="Edit Institution"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-4 mt-6">
          <div className="text-sm text-gray-600 font-medium">
            Showing <span className="text-gray-900">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
            <span className="text-gray-900">{Math.min(currentPage * itemsPerPage, sortedInstitutions.length)}</span> of{' '}
            <span className="text-gray-900">{sortedInstitutions.length}</span> institutions
          </div>
          
          <div className="flex items-center bg-white rounded-full p-1.5 shadow-sm border border-gray-200">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <div className="flex items-center gap-1 mx-2">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-all duration-200 ${
                      currentPage === pageNum
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {sortedInstitutions.length === 0 && (
        <div className="text-center py-12">
          <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No institutions found</h3>
          <p className="text-gray-500 mb-4">
            {hasActiveFilters 
              ? 'Try adjusting your filters or search terms.'
              : 'Get started by adding your first education institution.'}
          </p>
          {!hasActiveFilters && (
            <button
              onClick={() => {
                setEditingInstitution(null);
                setShowInstitutionForm(true);
              }}
              className="btn btn-admin-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Institution
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EducationInstitutionsTab;

